import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface Notification {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  date: string;
  type: 'info' | 'warning' | 'success' | 'error';
  priority: 'high' | 'medium' | 'low';
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private _http = inject(HttpClient);
  private _notifications$ = new BehaviorSubject<Notification[]>([]);

  // Default notifications (fallback)
  private defaultNotifications: Notification[] = [
    {
      id: '1',
      title: 'Calendário atualizado do semestre',
      message: 'Confira prazos de inscrição e provas.',
      isRead: false,
      date: new Date().toISOString(),
      type: 'info',
      priority: 'medium'
    },
    {
      id: '2',
      title: 'Manutenção de rede no campus',
      message: 'Intermitência 22:00–23:30.',
      isRead: false,
      date: new Date().toISOString(),
      type: 'warning',
      priority: 'high'
    },
    {
      id: '3',
      title: 'Novo material da disciplina',
      message: 'Processos Químicos - Aula 5.',
      isRead: false,
      date: new Date().toISOString(),
      type: 'info',
      priority: 'low'
    }
  ];

  constructor() {
    this.loadNotifications();
  }

  get notifications$(): Observable<Notification[]> {
    return this._notifications$.asObservable();
  }

  get notifications(): Notification[] {
    return this._notifications$.value;
  }

  get unreadNotifications(): Notification[] {
    return this._notifications$.value.filter(n => !n.isRead);
  }

  get unreadCount(): number {
    return this.unreadNotifications.length;
  }

  private loadNotifications(): void {
    // Try to load from backend first
    this._http.get<any>(`${environment.url}/api/dashboard`)
      .subscribe({
        next: (response) => {
          if (response.data?.announcements) {
            const notifications = response.data.announcements.map((announcement: any) => ({
              id: announcement.id,
              title: announcement.title,
              message: announcement.content,
              isRead: announcement.isRead,
              date: announcement.date,
              type: announcement.type || 'info',
              priority: announcement.priority || 'medium'
            }));
            this._notifications$.next(notifications);
          } else {
            this._notifications$.next(this.defaultNotifications);
          }
        },
        error: () => {
          // Fallback to default notifications
          this._notifications$.next(this.defaultNotifications);
        }
      });
  }

  markAsRead(notificationId: string): Observable<any> {
    const notifications = this._notifications$.value;
    const notification = notifications.find(n => n.id === notificationId);

    if (notification && !notification.isRead) {
      notification.isRead = true;
      this._notifications$.next([...notifications]);
    }

    // Call backend service
    return this._http.post<any>(`${environment.url}/api/dashboard/announcements/${notificationId}/read`, {});
  }

  markAllAsRead(): void {
    const notifications = this._notifications$.value;
    const unreadNotifications = notifications.filter(n => !n.isRead);

    if (unreadNotifications.length > 0) {
      // Mark all as read locally
      notifications.forEach(n => n.isRead = true);
      this._notifications$.next([...notifications]);

      // Call backend service for each unread notification
      unreadNotifications.forEach(notification => {
        this._http.post<any>(`${environment.url}/api/dashboard/announcements/${notification.id}/read`, {})
          .subscribe({
            next: () => console.log('Notification marked as read'),
            error: (error) => console.error('Error marking notification as read:', error)
          });
      });
    }
  }

  refreshNotifications(): void {
    this.loadNotifications();
  }

  getNotifications(): Observable<Notification[]> {
    return this.notifications$;
  }
}
